import Button from 'devextreme-react/button';
import LoadIndicator from 'devextreme-react/load-indicator';
import SelectBox from 'devextreme-react/select-box';
import TextBox from 'devextreme-react/text-box';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { showErrorToast } from '@jarvis/platform-ui';
import {
  canReorderPinnedCatalog,
  compareCatalogEntries,
  matchesCatalogFilter,
  MY_APPS_CATEGORY_FILTERS,
  type MyAppsCatalogFilter
} from '../../../apps/myAppsCatalog';
import {
  getPermittedSearchEntries,
  searchMyApps,
  type MyAppsSearchResult
} from '../../../apps/myAppsSearch';
import { MyAppsIcon } from '../../../apps/myAppsIcon';
import { launchMyAppsEntry } from '../../../apps/myAppsLaunch';
import { useMyAppsStore } from '../../../apps/useMyAppsStore';
import { useAuthStore } from '../../../auth/useAuthStore';
import { useSiteCapabilitiesStore } from '../../../site/useSiteCapabilitiesStore';
import '../../../apps/utilityApps.css';

// DevExtreme diffs options by identity, so keep filter props out of the render body.
const CATEGORY_FILTER_OPTIONS = [...MY_APPS_CATEGORY_FILTERS];
const FILTER_INPUT_ATTR = { 'aria-label': 'Filter apps' };

export function AppStorePage() {
  const navigate = useNavigate();
  const roles = useAuthStore((s) => s.roles);
  const analyticsEnabled = useSiteCapabilitiesStore((s) => s.analyticsEnabled);
  const status = useMyAppsStore((s) => s.status);
  const error = useMyAppsStore((s) => s.error);
  const pinnedToolIds = useMyAppsStore((s) => s.pinnedToolIds);
  const externalApps = useMyAppsStore((s) => s.externalApps);
  const saving = useMyAppsStore((s) => s.saving);
  const load = useMyAppsStore((s) => s.load);
  const togglePin = useMyAppsStore((s) => s.togglePin);
  const movePin = useMyAppsStore((s) => s.movePin);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<MyAppsCatalogFilter>('All');

  useEffect(() => {
    void load();
  }, [load]);

  const entries = useMemo(
    () => getPermittedSearchEntries(roles, externalApps, analyticsEnabled),
    [analyticsEnabled, externalApps, roles]
  );
  const pinnedSet = useMemo(
    () => new Set(pinnedToolIds.map((id) => id.toLowerCase())),
    [pinnedToolIds]
  );

  const filtered = useMemo(() => {
    const searched = searchMyApps(entries, query);
    const next = searched.filter((entry) => matchesCatalogFilter(entry, category, pinnedSet));
    if (query.trim()) return next;
    return [...next].sort((left, right) =>
      compareCatalogEntries(left, right, pinnedSet, pinnedToolIds)
    );
  }, [category, entries, pinnedSet, pinnedToolIds, query]);

  const allowPinReorder = canReorderPinnedCatalog(category, query);

  async function onTogglePin(entry: MyAppsSearchResult, pinned: boolean) {
    try {
      await togglePin(entry.id, pinned);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not update pinned apps.');
    }
  }

  async function onMovePin(entry: MyAppsSearchResult, direction: -1 | 1) {
    try {
      await movePin(entry.id, direction);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not reorder pinned apps.');
    }
  }

  return (
    <div className="user-settings-sections my-apps-store my-apps-store--settings">
      <section className="theme-preferences theme-preferences--first" aria-labelledby="my-apps-heading">
        <h3 id="my-apps-heading" className="theme-preferences__title">
          My Apps
        </h3>
        <p className="theme-preferences__hint">
          Browse every app your role can use, including custom Microsoft 365 links. Pin extra
          shortcuts for the sidebar, dashboard, and Ctrl+K launcher.
        </p>
        <div className="my-apps-store__filters">
          <TextBox
            stylingMode="outlined"
            value={query}
            placeholder="Search apps..."
            mode="search"
            showClearButton
            width={220}
            onValueChanged={(e) => setQuery(String(e.value ?? ''))}
          />
          <SelectBox
            stylingMode="outlined"
            dataSource={CATEGORY_FILTER_OPTIONS}
            value={category}
            width={150}
            inputAttr={FILTER_INPUT_ATTR}
            onValueChanged={(e) => setCategory(String(e.value) as MyAppsCatalogFilter)}
          />
        </div>
      </section>

      {status === 'loading' || status === 'idle' ? (
        <div className="my-apps-store__empty">
          <LoadIndicator visible />
        </div>
      ) : null}

      {status === 'error' ? (
        <div className="my-apps-store__empty">
          <p>{error ?? 'Failed to load apps.'}</p>
          <Button text="Retry" type="default" onClick={() => void load()} />
        </div>
      ) : null}

      {status === 'ready' && filtered.length === 0 ? (
        <div className="my-apps-store__empty">No apps match your filters.</div>
      ) : null}

      {status === 'ready' && filtered.length > 0 ? (
        <div className="my-apps-store__grid" role="list" aria-label="My Apps">
          {filtered.map((item) => {
            const isPinned = pinnedSet.has(item.id.toLowerCase());
            const pinIndex = pinnedToolIds.findIndex(
              (id) => id.toLowerCase() === item.id.toLowerCase()
            );
            const showReorder = allowPinReorder && isPinned && pinIndex >= 0;
            return (
              <article
                key={item.id}
                className={['my-apps-store__card', isPinned ? 'my-apps-store__card--pinned' : '']
                  .filter(Boolean)
                  .join(' ')}
                role="listitem"
              >
                <div className="my-apps-store__card-main">
                  <div className="my-apps-store__card-title">
                    <MyAppsIcon icon={item.icon} />
                    <span>{item.title}</span>
                  </div>
                  <p className="my-apps-store__card-desc">{item.description}</p>
                  <div className="my-apps-store__meta">
                    <span className="my-apps-store__badge">{item.category}</span>
                    <span className="my-apps-store__badge">
                      {item.kind === 'tool' ? 'Tool' : item.kind === 'link' ? 'Link' : 'App'}
                    </span>
                    {isPinned ? <span className="utility-app__muted">Pinned shortcut</span> : null}
                  </div>
                </div>
                <div className="my-apps-store__card-actions">
                  {showReorder ? (
                    <div className="my-apps-store__reorder">
                      <Button
                        icon="chevronup"
                        stylingMode="text"
                        disabled={saving || pinIndex <= 0}
                        hint={`Move ${item.title} up`}
                        onClick={() => void onMovePin(item, -1)}
                      />
                      <Button
                        icon="chevrondown"
                        stylingMode="text"
                        disabled={saving || pinIndex >= pinnedToolIds.length - 1}
                        hint={`Move ${item.title} down`}
                        onClick={() => void onMovePin(item, 1)}
                      />
                    </div>
                  ) : null}
                  <Button
                    text={isPinned ? 'Unpin' : 'Pin'}
                    stylingMode={isPinned ? 'contained' : 'outlined'}
                    disabled={saving}
                    hint={isPinned ? `Unpin ${item.title}` : `Pin ${item.title}`}
                    onClick={() => void onTogglePin(item, !isPinned)}
                  />
                  <Button
                    text="Open"
                    type="default"
                    stylingMode="contained"
                    hint={`Open ${item.title}`}
                    onClick={() => {
                      if (!launchMyAppsEntry(item, navigate)) {
                        showErrorToast('Could not open this app.');
                        return;
                      }
                    }}
                  />
                </div>
              </article>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
